/** Hooks over the device's DecisionStore: every readable decision, live-ish, plus the store. */
import { useQuery } from '@tanstack/react-query'
import type { DecisionRecord } from '@viko/protocol'
import { useEffect, useMemo, useSyncExternalStore } from 'react'
import { type Backend, useBackend } from '../../lib/backend.js'
import { type DecisionBackend, DecisionStore, decisionStoreFor } from '../../lib/decisions.js'
import { activePoll } from '../../lib/query-client.js'
import { createMockDecisionStore } from '../../mock/decisions.js'

/** The encrypted account's backend: entities, signed dispatch, sealed uploads. */
export function backendDecisions(b: Backend): DecisionBackend {
  return {
    entities: () => b.entities as never,
    dispatch: (kind, machineId, payload, opts) =>
      b.dispatch(kind, machineId, payload as never, opts ?? {}),
    deviceKeyId: () => b.deviceKeyId,
  }
}

/** Mock builds (no content keys) get an in-memory stand-in with a simulated asking agent. */
let mockFactory: ((b: Backend) => DecisionStore) | undefined
const mockListeners = new Set<() => void>()
export function setMockDecisionFactory(f: (b: Backend) => DecisionStore) {
  mockFactory = f
  for (const l of mockListeners) l()
}
// Inline literal: production builds drop this call and tree-shake the (side-effect free) mock.
if (import.meta.env.VITE_VIKO_MOCK === '1') setMockDecisionFactory(createMockDecisionStore)

export function useDecisionStore(): DecisionStore | undefined {
  const b = useBackend()
  const ents = b.entities
  const mock = useSyncExternalStore(
    (cb) => {
      mockListeners.add(cb)
      return () => mockListeners.delete(cb)
    },
    () => mockFactory,
  )
  return useMemo(() => {
    if (ents) return decisionStoreFor(ents, () => new DecisionStore(backendDecisions(b)))
    if (mock) return decisionStoreFor(b, () => mock(b))
    return undefined
  }, [b, ents, mock])
}

export const DECISIONS_KEY = ['decisions'] as const

export function useDecisions() {
  const store = useDecisionStore()
  const rev = useSyncExternalStore(
    store?.subscribe ?? (() => () => {}),
    store?.version ?? (() => 0),
  )
  const q = useQuery({
    queryKey: [...DECISIONS_KEY, !!store],
    queryFn: () => store!.list(),
    enabled: !!store,
    // Thread replies stream in while a conversation is active; quiet otherwise.
    ...activePoll((query) =>
      (query.state.data as DecisionRecord[] | undefined)?.some((d) =>
        d.thread.some((m) => m.author === 'user' && Date.now() - m.at < 5 * 60_000),
      )
        ? 1500
        : 60_000,
    ),
  })
  const refetch = q.refetch
  useEffect(() => {
    if (rev) void refetch()
  }, [rev, refetch])
  return { ...q, store }
}
